import { Stack, Table } from "@mantine/core"
import { use } from "react"
import { useTranslation } from "react-i18next"
import { AppLink, AppPagination, ButtonDetails, DateTime, SortHeader } from "@/components"
import { useAppPaginationQuery, useQueryState } from "@/hooks"
import { buildSessionURL, buildUserSessionURL, type IAPIParams, UserSessionContext } from "@/utils"
import { SurveyButton } from "../surveys"
import { useModalUserSessionDetails } from "../surveys/details/ModalUserSessionDetails"
import { SessionState } from "./SessionState"
import { UserSessionsTable } from "./UserSessionsTable"

export function UserSessions(params: IAPIParams) {
  const { t } = useTranslation()
  const sort = (useQueryState("sort")[0] || "-dtstart") as string
  const paginate = useAppPaginationQuery(10)

  return (
    <Stack>
      <UserSessionsTable
        headers={[
          <SortHeader key="title" field="title">
            {t("Title")}
          </SortHeader>,
          <SortHeader key="dtstart" field="dtstart" defaultActive>
            {t("Dates")}
          </SortHeader>,
          <SortHeader key="state" field="state" justify="flex-end">
            {t("State")}
          </SortHeader>,
          "",
        ]}
        {...params}
        transform={(data) => {
          const desc = sort.startsWith("-")
          const sortField = sort.startsWith("-") ? sort.slice(1) : sort
          return data.toSorted((a, b) => {
            let aValue: string | number = ""
            let bValue: string | number = ""
            if (sortField == "title") {
              aValue = a.session.title
              bValue = b.session.title
            } else if (sortField == "dtstart") {
              aValue = a.session.dtstart || 0
              bValue = b.session.dtstart || 0
            } else if (sortField == "state") {
              aValue = a.state
              bValue = b.state
            }
            if (aValue < bValue) return desc ? 1 : -1
            if (aValue > bValue) return desc ? -1 : 1
            return 0
          })
        }}
        limit={paginate.limit}
        offset={paginate.offset}
        setTotal={paginate.setTotal}
      >
        <UserSessionRow />
      </UserSessionsTable>
      <AppPagination {...paginate} />
    </Stack>
  )
}

function UserSessionRow() {
  const usersession = use(UserSessionContext)!
  const { session } = usersession
  const openDetails = useModalUserSessionDetails(usersession)
  return (
    <>
      <Table.Td>
        <AppLink
          size="lg"
          href={usersession.finished ? buildUserSessionURL(usersession) : buildSessionURL(session, "quiz")}
        >
          {session.title}
        </AppLink>
      </Table.Td>
      <Table.Td>
        <DateTime timestamp={session.dtstart} format="D MMM" /> -{" "}
        <DateTime timestamp={session.dtfinish} format="D MMM YYYY" />
      </Table.Td>
      <Table.Td ta="right">
        <SessionState state={usersession.state} />
      </Table.Td>
      <Table.Td ta="right">
        {usersession.state != "canceled" && (
          <SurveyButton usersession={usersession} hideTrigger={usersession.state === "finished"} />
        )}
        {usersession.state === "finished" && <ButtonDetails onClick={openDetails} />}
      </Table.Td>
    </>
  )
}
